import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Modal } from 'react-bootstrap';
import ImageWithFallback from './ui/ImageWithFallback';
import 'boxicons/css/boxicons.min.css';

export interface TeamMember {
  _id: string;
  name: string;
  position: string;
  image: string;
  bio: string;
  department: string;
  order: number;
  isActive: boolean;
}

// Static team members data
const staticTeamMembers: TeamMember[] = [
  {
    _id: '1',
    name: 'Dr. Ahmed Hassan',
    position: 'Directeur Général',
    image: '/images/teacher1.jpg',
    bio: 'Directeur avec plus de 20 ans d\'expérience dans l\'éducation supérieure.',
    department: 'Administration',
    order: 1,
    isActive: true
  },
  {
    _id: '2',
    name: 'Prof. Fatima Bennani',
    position: 'Responsable Programmes Académiques',
    image: '/images/teacher2.jpg',
    bio: 'Professeur expérimentée en gestion et management.',
    department: 'Académique',
    order: 2,
    isActive: true
  },
  {
    _id: '3',
    name: 'Dr. Rachid El Mansouri',
    position: 'Responsable Partenariats',
    image: '/images/teacher3.jpg',
    bio: 'Expert en relations internationales et partenariats académiques.',
    department: 'Partenariats',
    order: 3,
    isActive: true
  },
  {
    _id: '4',
    name: 'Dr. Noureddine Jazouli',
    position: 'Professeur de Management',
    image: '/images/teacher4.jpg',
    bio: 'Spécialiste en stratégie d\'entreprise et management.',
    department: 'Académique',
    order: 4,
    isActive: true
  }
];

interface TeamSectionProps {
  title?: string;
  department?: string;
  limit?: number;
  showFeaturedOnly?: boolean;
}

const TeamSection: React.FC<TeamSectionProps> = ({ 
  title = "Notre Équipe Pédagogique",
  department,
  limit,
  showFeaturedOnly = false
}) => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  // Filter static team members
  let filteredMembers = [...staticTeamMembers];
  
  if (department) {
    filteredMembers = filteredMembers.filter(member => 
      member.department === department
    );
  }
  
  if (limit && filteredMembers.length > limit) {
    filteredMembers = filteredMembers.slice(0, limit);
  }

  const handleShowDetails = (member: TeamMember) => {
    setSelectedMember(member);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <section className="py-5 bg-light">
      <Container>
        <h2 className="text-center mb-5" data-aos="fade-up">{title}</h2>
        <Row className="g-4">
          {filteredMembers.map((member, index) => (
            <Col key={member._id || index} xs={12} sm={6} md={4} lg={3}>
              <Card 
                className="h-100 team-card" 
                data-aos="fade-up" 
                data-aos-delay={100 + (index * 50)}
              >
                <div className="text-center p-3">
                  <div className="mb-3 mx-auto" style={{ width: '120px', height: '120px', borderRadius: '50%', overflow: 'hidden' }}>
                    <ImageWithFallback 
                      src={member.image}
                      alt={member.name}
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                      fallbackIcon={<i className='bx bxs-user text-secondary' style={{ fontSize: '3rem' }}></i>}
                    />
                  </div>
                  <Card.Title as="h5">{member.name}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">{member.position}</Card.Subtitle>
                  
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={() => handleShowDetails(member)}
                    className="mt-3"
                  >
                    Voir le profil
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Team Member Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        {selectedMember && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedMember.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="d-flex flex-column flex-md-row">
                <div className="text-center mb-4 mb-md-0 me-md-4" style={{ minWidth: '150px' }}>
                  <div className="mb-3 mx-auto" style={{ width: '150px', height: '150px', borderRadius: '50%', overflow: 'hidden' }}>
                    <ImageWithFallback 
                      src={selectedMember.image}
                      alt={selectedMember.name}
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                      fallbackIcon={<i className='bx bxs-user text-secondary' style={{ fontSize: '4rem' }}></i>}
                    />
                  </div>
                  <h5 className="mb-1">{selectedMember.name}</h5>
                  <p className="text-muted">{selectedMember.position}</p>
                </div>
                
                <div className="flex-grow-1">
                  <h4>Biographie</h4>
                  <p style={{ whiteSpace: 'pre-line' }}>{selectedMember.bio}</p>
                  
                  {/* Education */}
                  {selectedMember.education && selectedMember.education.length > 0 && (
                    <div className="mt-4">
                      <h4>Formation</h4>
                      <ul className="list-unstyled">
                        {selectedMember.education.map((edu, index) => (
                          <li key={index} className="mb-3">
                            <div className="d-flex">
                              <i className='bx bxs-graduation me-2 mt-1 text-primary' style={{ fontSize: '1.2rem' }}></i>
                              <div>
                                <h5 className="mb-1">{edu.degree}</h5>
                                <p className="mb-0">{edu.institution}</p>
                                <small className="text-muted">{edu.year}</small>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Fermer
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </section>
  );
};

export default TeamSection;